import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { CustomAutocomplete, CustomTextField, SampleCheckbox, SingleFormBoxScene } from '@/shared/components/ui';
import { IPaymentMethodProveedor } from '@/shared/interfaces/erp/system-settings';
import { newPaymentMethodProveedorSchema } from '@/shared/utils';
import { TipoContratoEnumChoice, TipoFormaPagoEnumChoice } from '@/shared';
import { EntidadesFinancieras, Tarjeta } from '@/shared/interfaces';
import { useFetchTarjetas } from '@/shared/actions/erp/administration/tarjeta.actions';
import { useCreatePaymentMethodProveedor, useUpdatePaymentMethodProveedor } from '@/shared/actions/erp/payment-methods-proveedor.actions';
import { useFetchEntidadesFinancieras } from '@/shared/actions/erp';


export type SavePaymentMethodProveedorProps = {
  editPaymentMethodProveedor?: IPaymentMethodProveedor;
  titlePage: string;
};

type SaveFormData = {
  codigo?: string;
  nombre: string;
  alias?: string;
  tipo_contrato: string;
  forma_pago: string;
  estado: boolean;
  entidad_financiera: string;
  tarjeta?: string;
  codigo_sri?: string
};

const returnUrlPayment = '/parametrizacion/metodo-pago-proveeedor';

const SavePaymentMethodProveedor: React.FC<SavePaymentMethodProveedorProps> = ({
  titlePage,
  editPaymentMethodProveedor,
}) => {
  const navigate = useNavigate();

  const [shrink, setShrink] = useState(false);
  const [showTarjetas, setShowTarjetas] = useState(false);


  ///* mutations
  const createPaymentMethodProveedorMutation = useCreatePaymentMethodProveedor({
    navigate,
    returnUrl: returnUrlPayment
  });
  const updatePaymentMethodProveedorMutation = useUpdatePaymentMethodProveedor({
    navigate,
    returnUrl: returnUrlPayment
  }
  );
  const metodosPagoOptions = Object.entries(TipoContratoEnumChoice).map(
    ([key, value]) => ({ label: key, value })
  );
  const formaPagoOptions = Object.entries(TipoFormaPagoEnumChoice).map(
    ([key, value]) => ({ label: key, value })
  );
  const onChangeEntidadFinanciera = (value: string) => {
    form.setValue('entidad_financiera', value);
  }
  const onChangeTarjetas = (value: string) => {
    form.setValue('tarjeta', value);
  }
  ///* form
  const form = useForm<SaveFormData>({
    resolver: yupResolver(newPaymentMethodProveedorSchema),
    defaultValues: { estado: true },
  });
  const {
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = form;

  ///* effects
  useEffect(() => {
    if (!editPaymentMethodProveedor?.id_formapago_proveedor) return;
    setShrink(true);
    reset({ ...editPaymentMethodProveedor } as any);
  }, [editPaymentMethodProveedor]);

  ///* handlers
  const onSave = async () => {
    if (!isValid) return;

    ///* edit
    ///* edit
    if (editPaymentMethodProveedor?.id_formapago_proveedor) {
      return updatePaymentMethodProveedorMutation.mutate({
        id: editPaymentMethodProveedor.id_formapago_proveedor,
        data: form.getValues() as any,
      });
    }


    ///* create
    return createPaymentMethodProveedorMutation.mutate(form.getValues() as any,);
  };
  const { data: EntidadesFinancierasPaging, isLoading: isLoadingEntidadesFinancieras } = useFetchEntidadesFinancieras({
    page_size: 600,
  });
  const { data: TarjetasPaging, isLoading: isLoadingTarjetas } = useFetchTarjetas({
    page_size: 600,
  });


  return (
    <SingleFormBoxScene
      titlePage={titlePage}
      onCancel={() => {
        navigate(returnUrlPayment);
      }}
      onSave={handleSubmit(onSave)}
    >

      {/*   <CustomTextField
        label="Codigo"
        name='codigo'
        control={form.control}
        error={errors.codigo}
        helperText={errors.codigo?.message}
        shrink={shrink}
      /> */}
      <CustomTextField
        label="Nombre"
        name='nombre'
        control={form.control}
        error={errors.nombre}
        helperText={errors.nombre?.message}
        shrink={shrink}
      />
      <CustomTextField
        label="Código SRI"
        name='codigo_sri'
        control={form.control}
        error={errors.codigo_sri}
        helperText={errors.codigo_sri?.message}
        shrink={shrink}
      />
      <CustomTextField
        label="Alias"
        name='alias'
        control={form.control}
        error={errors.alias}
        helperText={errors.alias?.message}
        shrink={shrink}
      />
      <CustomAutocomplete
        label="Tipo de contrato"
        name="tipo_contrato"
        control={form.control}
        defaultValue={form.getValues().tipo_contrato || ''}
        error={errors.tipo_contrato}
        helperText={errors.tipo_contrato?.message}
        options={metodosPagoOptions}
        isLoadingData={false}
        valueKey="value"
        optionLabelForEdit={form.getValues().tipo_contrato}
      />

      <CustomAutocomplete
        label="forma pago"
        name="forma_pago"
        control={form.control}
        defaultValue={form.getValues().forma_pago || ''}
        error={errors.forma_pago}
        helperText={errors.forma_pago?.message}
        options={formaPagoOptions}
        isLoadingData={false}
        valueKey="value"
        optionLabelForEdit={form.getValues().forma_pago}
      />

      <CustomAutocomplete<EntidadesFinancieras>
        label="Entidades financieras"
        name="entidades_fianncieras"
        //// options
        options={EntidadesFinancierasPaging?.data.data || []}
        // label & search by name
        valueKey="nombre"
        actualValueKey='codigo'
        //
        defaultValue={form.getValues().entidad_financiera || ''}
        optionLabelForEdit={form.getValues().entidad_financiera || ''}
        isLoadingData={isLoadingEntidadesFinancieras}
        //// validation
        control={form.control}
        error={errors.entidad_financiera}
        helperText={errors.entidad_financiera?.message}
        onChangeValue={onChangeEntidadFinanciera}
      />
      <SampleCheckbox
        label="¿Crear metodo de pago con tarjetas?"
        name="llevaTarjetas"
        control={form.control}
        defaultValue={false}
        onChangeValue={(e) => {
          setShowTarjetas(e!);
        }}
      />
      {showTarjetas && (
        <CustomAutocomplete<Tarjeta>
          label="Tarjetas"
          name="tarjetas"
          options={TarjetasPaging?.data.data || []}
          valueKey="nombre"
          actualValueKey='codigo'
          defaultValue={form.getValues().tarjeta || ''}
          optionLabelForEdit={form.getValues().tarjeta || ''}
          isLoadingData={isLoadingTarjetas}
          control={form.control}
          error={errors.tarjeta}
          helperText={errors.tarjeta?.message}
          onChangeValue={onChangeTarjetas}
        />
      )}
      <SampleCheckbox
        label="Estado"
        name="estado"
        control={form.control}
        defaultValue={!!form.getValues().estado}
        isState
      />
    </SingleFormBoxScene>
  );
};

export default SavePaymentMethodProveedor;